// Frame-spike tracking (north star: observability by default). Records
// long frames with a phase tag and keeps the last runs' worth in a
// localStorage ring buffer, so "it felt laggy" comes with data attached.
//
// Read a report in the browser console:  __c130Perf()

export type PerfSpike = {
  t: number; // ms since mission start
  ms: number; // frame duration
  tag: string; // what phase the mission was in
};

export type PerfRun = {
  startedAt: string; // ISO time
  mission: string;
  spikes: PerfSpike[];
  marks: Array<{ t: number; label: string }>;
  client?: string; // anonymous per-browser id (telemetry grouping)
  ua?: string; // coarse platform hint for cross-machine comparison
};

const STORE_KEY = "c130_perf_log";
const CLIENT_KEY = "c130_client_id";
const TELEMETRY_URL = "/api/telemetry";

// A stable anonymous id per browser — lets us group a tester's runs without
// knowing who they are. No PII; a random tag persisted in localStorage.
function clientId(): string {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 10);
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}
const SPIKE_THRESHOLD_MS = 70; // ~4 dropped frames at 60fps
const MAX_SPIKES_PER_RUN = 60;
const MAX_RUNS = 8;

export class PerfTracker {
  private readonly run: PerfRun;
  private readonly t0: number;
  private phase = "boot";
  private frames = 0;
  private worst = 0;

  constructor(missionName: string) {
    this.t0 = performance.now();
    this.run = {
      startedAt: new Date().toISOString(),
      mission: missionName,
      spikes: [],
      marks: [],
    };
    this.mark("engine:constructed");
    // One global report hook, latest tracker wins (browser only)
    if (typeof window !== "undefined") {
      (window as unknown as { __c130Perf?: () => PerfRun[] }).__c130Perf = () => readRuns();
    }
  }

  // Tag the current phase so spikes say WHERE they happened
  setPhase(phase: string): void {
    if (phase === this.phase) return;
    this.phase = phase;
    this.mark(`phase:${phase}`);
  }

  mark(label: string): void {
    this.run.marks.push({ t: Math.round(performance.now() - this.t0), label });
  }

  frame(dtMs: number): void {
    this.frames++;
    if (dtMs > this.worst) this.worst = dtMs;
    if (dtMs < SPIKE_THRESHOLD_MS) return;
    if (this.run.spikes.length >= MAX_SPIKES_PER_RUN) return;
    this.run.spikes.push({
      t: Math.round(performance.now() - this.t0),
      ms: Math.round(dtMs),
      tag: this.phase,
    });
  }

  // Persist on mission end. Console summary keeps playtests honest.
  flush(): void {
    this.mark(`end (${this.frames} frames, worst ${Math.round(this.worst)}ms)`);
    const runs = readRuns();
    runs.unshift(this.run);
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(runs.slice(0, MAX_RUNS)));
    } catch {
      // Storage full/blocked — the console summary below still lands
    }
    if (this.run.spikes.length > 0) {
      console.info(
        `[perf] ${this.run.mission}: ${this.run.spikes.length} spike(s) >${SPIKE_THRESHOLD_MS}ms, worst ${Math.round(this.worst)}ms.`,
        "Run __c130Perf() for details.",
      );
    }
    this.beacon();
  }

  // Fire-and-forget the report to /api/telemetry. sendBeacon survives the
  // page unload that a "return to menu" or tab-close can trigger, and never
  // blocks the game. Telemetry failure is silent by design.
  private beacon(): void {
    try {
      this.run.client = clientId();
      this.run.ua = navigator.userAgent.slice(0, 120);
      const body = JSON.stringify(this.run);
      const sent = navigator.sendBeacon?.(TELEMETRY_URL, body);
      if (!sent) {
        void fetch(TELEMETRY_URL, { method: "POST", body, keepalive: true }).catch(() => {});
      }
    } catch {
      // No beacon support / offline — the localStorage copy remains
    }
  }
}

export function readRuns(): PerfRun[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as PerfRun[]) : [];
  } catch {
    return [];
  }
}
